"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getTimeDifference = exports["default"] = exports.dateFormats = void 0;
var _luxon = require("luxon");
var _react = require("react");
var _TimezoneContext = require("../_contexts/TimezoneContext");
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// Luxon formats https://moment.github.io/luxon/#/formatting?id=table-of-tokens
// MM & LL can be used interchangeably for en
var dateFormats = {
  // Luxon formats
  TIME: "HH:mm",
  BASE_DATE_FORMAT: "HH:mm/dd",
  FULL_DATE_TIME: "dd/MM/yyyy HH:mm",
  TIME_DATE_SHORT: "HH:mm dd/MM",
  DATE: "dd/MM/yyyy",
  DATE_SHORT: "dd/MM",
  MONTH_YEAR_SHORT: "MM/yy",
  YEAR: "yyyy"
};
exports.dateFormats = dateFormats;
var formatISODate = function formatISODate(isoString, format, zone) {
  return isoString ? _luxon.DateTime.fromISO(isoString).setZone(zone).toFormat(format) : "";
};
var formatUnixDate = function formatUnixDate(seconds, format, zone) {
  return seconds ? _luxon.DateTime.fromMillis(parseInt(seconds) * 1000).setZone(zone).toFormat(format) : "";
};
var getJSDateFromUnixDate = function getJSDateFromUnixDate(seconds, zone) {
  return seconds ? _luxon.DateTime.fromMillis(parseInt(seconds) * 1000).setZone(zone).toJSDate() : "";
};
var getDateTimeFromUnixDate = function getDateTimeFromUnixDate(seconds, zone) {
  return seconds ? _luxon.DateTime.fromMillis(parseInt(seconds) * 1000).setZone(zone) : "";
};
var getFromJSDate = function getFromJSDate(date, zone) {
  return _luxon.DateTime.fromJSDate(date).setZone(zone);
};
var getRelativeDateTime = function getRelativeDateTime(value, unit, zone) {
  var nowDateTime = _luxon.DateTime.fromMillis(Date.now()).setZone(zone);
  return nowDateTime.plus(_defineProperty({}, unit, value));
};
var getSecondsFromJSDate = function getSecondsFromJSDate(date, zone) {
  return getFromJSDate(date, zone).toSeconds();
};

/**
 * Computes the day difference for an interval.
 *
 * * "toUnit" nd "fromUnit" need to be string time units like "seconds",
 *   "minutes" etc.
 * * "toValue" and "fromValue" need to be JavaScript "Date" instances if their
 *   matching unit variable is missing, or a numbers otherwise. The numbers
 *   refer to the relative time units that need to be added to the "Now" date.
 *
 * @param {Object}
 * @returns {Number}
 */
var getTimeDifference = function getTimeDifference(_ref) {
  var toValue = _ref.toValue,
    toUnit = _ref.toUnit,
    fromValue = _ref.fromValue,
    fromUnit = _ref.fromUnit,
    _ref$outputUnit = _ref.outputUnit,
    outputUnit = _ref$outputUnit === void 0 ? "days" : _ref$outputUnit,
    zone = _ref.zone;
  var nowDate = new Date();
  var fromDateTime = fromUnit ? _luxon.DateTime.fromJSDate(nowDate).setZone(zone).plus(_defineProperty({}, fromUnit, fromValue)) : _luxon.DateTime.fromJSDate(fromValue).setZone(zone);
  var toDateTime = toUnit ? _luxon.DateTime.fromJSDate(nowDate).setZone(zone).plus(_defineProperty({}, toUnit, toValue)) : _luxon.DateTime.fromJSDate(toValue).setZone(zone);
  return toDateTime.diff(fromDateTime, outputUnit).toObject()[outputUnit];
};

/*
 * Return the current moment and some helper functions
 * based on the selected timezone or forcing to UTC if defined
 */
exports.getTimeDifference = getTimeDifference;
var useTimezoneDate = function useTimezoneDate() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      forceUTC: false
    },
    forceUTC = _ref2.forceUTC;
  var _useTimezone = (0, _TimezoneContext.useTimezone)(),
    timezone = _useTimezone.timezone;
  var formatISO = (0, _react.useCallback)(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return formatISODate.apply(void 0, args.concat([forceUTC ? _TimezoneContext.UTC : timezone]));
  }, [forceUTC, timezone]);
  var formatUnix = (0, _react.useCallback)(function () {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    return formatUnixDate.apply(void 0, args.concat([forceUTC ? _TimezoneContext.UTC : timezone]));
  }, [forceUTC, timezone]);
  var getRelative = (0, _react.useCallback)(function () {
    for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      args[_key3] = arguments[_key3];
    }
    return getRelativeDateTime.apply(void 0, args.concat([forceUTC ? _TimezoneContext.UTC : timezone]));
  }, [forceUTC, timezone]);
  var getTimeDiff = (0, _react.useCallback)(function (args) {
    return getTimeDifference(_objectSpread(_objectSpread({}, args), {}, {
      zone: forceUTC ? _TimezoneContext.UTC : timezone
    }));
  }, [forceUTC, timezone]);
  var getFromJS = (0, _react.useCallback)(function () {
    for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      args[_key4] = arguments[_key4];
    }
    return getFromJSDate.apply(void 0, args.concat([forceUTC ? _TimezoneContext.UTC : timezone]));
  }, [forceUTC, timezone]);
  var getJSFromUnix = (0, _react.useCallback)(function () {
    for (var _len5 = arguments.length, args = new Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
      args[_key5] = arguments[_key5];
    }
    return getJSDateFromUnixDate.apply(void 0, args.concat([forceUTC ? _TimezoneContext.UTC : timezone]));
  }, [forceUTC, timezone]);
  var getSecondsFromJS = (0, _react.useCallback)(function () {
    for (var _len6 = arguments.length, args = new Array(_len6), _key6 = 0; _key6 < _len6; _key6++) {
      args[_key6] = arguments[_key6];
    }
    return getSecondsFromJSDate.apply(void 0, args.concat([timezone]));
  }, [timezone]);
  var getDateTimeFromUnix = (0, _react.useCallback)(function () {
    for (var _len7 = arguments.length, args = new Array(_len7), _key7 = 0; _key7 < _len7; _key7++) {
      args[_key7] = arguments[_key7];
    }
    return getDateTimeFromUnixDate.apply(void 0, args.concat([forceUTC ? _TimezoneContext.UTC : timezone]));
  }, [forceUTC, timezone]);
  var getCurrentTime = (0, _react.useCallback)(function () {
    return _luxon.DateTime.now().setZone(forceUTC ? _TimezoneContext.UTC : timezone);
  }, [forceUTC, timezone]);
  return {
    timezone: timezone,
    getCurrentTime: getCurrentTime,
    formatISODate: formatISO,
    formatUnixDate: formatUnix,
    getRelativeDateTime: getRelative,
    getTimeDifference: getTimeDiff,
    getFromJSDate: getFromJS,
    getJSDateFromUnixDate: getJSFromUnix,
    getSecondsFromJSDate: getSecondsFromJS,
    getDateTimeFromUnixDate: getDateTimeFromUnix
  };
};
var _default = useTimezoneDate;
exports["default"] = _default;